import { useState, useEffect, useRef } from "react";
import { useLocalization } from "@fluent/react";
import { Alert } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import { ScrollingIframe } from "cerulean";

export interface SignedUrlIframeProps {
  endpoint: string;
  height?: string;
  setLoading?: (loading: boolean) => void;
  setError?: (error: any) => void;
  sendNotification?: (notification: any) => void;
  endpointIsUrl?: boolean;
  sandbox?: string;
  onLoad?: () => void;
}

export default function SignedUrlIframe({
  endpoint,
  height = "600px",
  setLoading = () => {},
  setError,
  sendNotification,
  endpointIsUrl,
  sandbox,
  onLoad,
}: SignedUrlIframeProps) {
  const { l10n } = useLocalization();
  const [src, setSrc] = useState("");
  const [action, setAction] = useState("");
  const [signedRequest, setSignedRequest] = useState("");
  const [postBody, setPostBody] = useState("");
  const [postBodyParameterName, setPostBodyParameterName] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const [target, setTarget] = useState("");
  const topFormRef = useRef<HTMLFormElement>(null);
  const hasSubmittedTopForm = useRef(false);
  const unavailableMessage = l10n.getString(
    "error-external-service-unavailable",
    null,
    "Sorry, this external service is not available at this time"
  );

  useEffect(() => {
    const fetchSignedUrl = async () => {
      setLoading(true);

      try {
        const response = await ApiHttp.fetch(endpoint, { endpointIsUrl });

        if (response.method === "post") {
          setAction(response.url);
          setTarget(response.target || "");
          setSignedRequest(response.signed_request || "");
          setPostBody(response.post_body || "");
          setPostBodyParameterName(
            response.post_body_parameter_name ||
              (response.post_body ? "post_body" : "")
          );
          setCode(
            response.post_body
              ? ""
              : new URL(response.url).searchParams.get("code")
          );
        } else {
          setSrc(response.url);
          setCode("");
        }
      } catch (err) {
        setCode("");
        setHasFailed(true);
        setError?.(err);

        if (sendNotification) {
          sendNotification({ type: "negative", text: unavailableMessage });
        }
      }

      setLoading(false);
    };

    fetchSignedUrl();
  }, []);

  useEffect(() => {
    if (target === "_top" && !hasSubmittedTopForm.current) {
      hasSubmittedTopForm.current = true;
      topFormRef.current?.submit();
    }
  }, [target]);

  if (code === null) {
    return null;
  }

  // standalone pages (e.g. /signed_url/...) have no other way to report the failure
  if (hasFailed && !setError && !sendNotification) {
    return (
      <Alert isActive kind="error" isDismissable={false}>
        {unavailableMessage}
      </Alert>
    );
  }

  // some providers can't run framed (e.g. their login cookies are dropped cross-site),
  // so the browser POSTs to them in the full window instead of the iframe
  if (target === "_top") {
    return (
      <form ref={topFormRef} action={action} method="POST" target="_top">
        {postBodyParameterName && (
          <input type="hidden" name={postBodyParameterName} value={postBody} />
        )}
      </form>
    );
  }

  return (
    <ScrollingIframe
      title="signedUrl"
      height={height}
      src={src}
      action={action}
      signedRequest={signedRequest}
      postBody={postBody}
      postBodyParameterName={postBodyParameterName}
      code={code}
      sandbox={sandbox}
      onLoad={onLoad}
    />
  );
}
